import type { StateCreator } from 'zustand';
import create from 'zustand';

/*
 * Command Center UI preferences persisted to localStorage.
 *
 * `loadConfig`/`saveConfig` are the save/load "script"; `useCcConfig` exposes the
 * config reactively. Extend `CcConfig` + `DEFAULT_CONFIG` to persist more prefs —
 * `loadConfig` merges over the defaults so older stored blobs stay compatible.
 */

export type ViewMode = 'board' | 'table';

export interface CcConfig {
  /** Event card size level (1–4). */
  cardSize: number;
  /** How the events listing is rendered. */
  viewMode: ViewMode;
  /** Auto-play the live stream on event-list cards (Live + live "My Events"). */
  autoplayCards: boolean;
  /** Auto-play the live stream on the sidebar Quick Switch cards. */
  autoplaySidebar: boolean;
  /** Show the sidebar Quick Switch feeds. Off, they are not mounted and make no requests. */
  showSidebarFeeds: boolean;
}

const STORAGE_KEY = 'cc.config';

const DEFAULT_CONFIG: CcConfig = {
  cardSize: 3,
  viewMode: 'board',
  autoplayCards: true,
  autoplaySidebar: true,
  showSidebarFeeds: true
};

export const loadConfig = (): CcConfig => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (raw) {
      return { ...DEFAULT_CONFIG, ...<Partial<CcConfig>>JSON.parse(raw) };
    }
  } catch {
    // Ignore malformed or blocked storage; fall back to defaults.
  }

  return DEFAULT_CONFIG;
};

export const saveConfig = (config: CcConfig): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // Ignore storage failures (private mode, quota, etc.).
  }
};

interface CcConfigStore extends CcConfig {
  setCardSize: (value: number) => void;
  setViewMode: (value: ViewMode) => void;
  setAutoplayCards: (value: boolean) => void;
  setAutoplaySidebar: (value: boolean) => void;
  setShowSidebarFeeds: (value: boolean) => void;
}

const config: StateCreator<CcConfigStore> = (set, get) => {
  const persist = (): void => {
    const { cardSize, viewMode, autoplayCards, autoplaySidebar, showSidebarFeeds } = get();

    saveConfig({ cardSize, viewMode, autoplayCards, autoplaySidebar, showSidebarFeeds });
  };

  return {
    ...loadConfig(),
    setCardSize: (value): void => {
      set({ cardSize: value });
      persist();
    },
    setViewMode: (value): void => {
      set({ viewMode: value });
      persist();
    },
    setAutoplayCards: (value): void => {
      set({ autoplayCards: value });
      persist();
    },
    setAutoplaySidebar: (value): void => {
      set({ autoplaySidebar: value });
      persist();
    },
    setShowSidebarFeeds: (value): void => {
      set({ showSidebarFeeds: value });
      persist();
    }
  };
};

export const useCcConfig = create<CcConfigStore>(config);
